import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function ExponentRootCalculator() {
  const [base, setBase] = useState("");
  const [exponent, setExponent] = useState("");
  const [number, setNumber] = useState("");
  const [root, setRoot] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculatePower = () => {
    const b = parseFloat(base);
    const exp = parseFloat(exponent);

    if (isNaN(b) || isNaN(exp)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    const res = Math.pow(b, exp);
    setResult(res);
    toast({
      title: "Calculation Complete",
      description: `${b}^${exp} = ${res}`,
    });
  };

  const calculateRoot = () => {
    const num = parseFloat(number);
    const r = parseFloat(root);

    if (isNaN(num) || isNaN(r) || r === 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers. Root cannot be zero.",
        variant: "destructive",
      });
      return;
    }

    const res = Math.pow(num, 1 / r);
    setResult(res);
    toast({
      title: "Calculation Complete",
      description: `${r}th root of ${num} = ${res.toFixed(4)}`,
    });
  };

  const reset = () => {
    setBase("");
    setExponent("");
    setNumber("");
    setRoot("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Exponent & Root Calculator" 
      calculatorPath="/calculator/exponent-root"
      category="math"
      subcategory="Basic Math"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Exponent & Root Calculator</CardTitle>
            <CardDescription>
              Calculate powers and roots
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="exponent">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="exponent">Exponent (Power)</TabsTrigger>
                <TabsTrigger value="root">Root</TabsTrigger>
              </TabsList>

              <TabsContent value="exponent" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="base">Base</Label>
                  <Input
                    id="base"
                    type="number"
                    step="any"
                    value={base}
                    onChange={(e) => setBase(e.target.value)}
                    placeholder="Enter base number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="exponent">Exponent</Label>
                  <Input
                    id="exponent"
                    type="number"
                    step="any"
                    value={exponent}
                    onChange={(e) => setExponent(e.target.value)}
                    placeholder="Enter power"
                  />
                </div>
                <Button onClick={calculatePower} className="w-full">Calculate Power</Button>
                
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-sm">Formula</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MathFormula block>{String.raw`a^n = a \times a \times ... \times a \text{ (n times)}`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="root" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="number">Number</Label>
                  <Input
                    id="number"
                    type="number"
                    step="any"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    placeholder="Enter number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="root">Root (2 for square root, 3 for cube root)</Label>
                  <Input
                    id="root"
                    type="number"
                    step="any"
                    value={root}
                    onChange={(e) => setRoot(e.target.value)}
                    placeholder="Enter root index"
                  />
                </div>
                <Button onClick={calculateRoot} className="w-full">Calculate Root</Button>
                
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-sm">Formula</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MathFormula block>{String.raw`\sqrt[n]{x} = x^{\frac{1}{n}}`}</MathFormula>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <Button onClick={reset} variant="outline" className="w-full">Reset All</Button>

            {result !== null && (
              <>
                <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-3xl font-bold break-all overflow-wrap-anywhere">{result.toExponential(4)}</p>
                      <p className="text-xl mt-2 break-all overflow-wrap-anywhere">{result.toFixed(4)}</p>
                    </div>
                    <CopyResultButton value={result.toExponential(4)} />
                  </div>
                </CardContent>
              </Card>

              <CalculatorSuperCalcCTA config={calculatorCTAs["exponent-root"]} trigger={result} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
